import type { Activity, News } from '~api';
import { api } from '~portal/api';
import { numberParam } from '~portal/lib/listing';
import { NEWS_FEED_KINDS } from './home';
import { liveQuery } from './live';
import type { LiveQuery } from './types';

// ---------------------------------------------------------------------------------------------
// The `/news` and `/updates` listings behind Home's "See more…" links (UI round 3, H2-310). Both
// page on the server — 9,000-odd stories are not fetched to page in the browser — so the URL holds
// the page number alone and every page is one `*.list` call.
// ---------------------------------------------------------------------------------------------

/** @desc Rows per page on both listings: the listings' default view size (D-5). */
export const FEED_PAGE_SIZE = 24;

/** @desc A feed listing's URL state, sparse: `page` only when past the first. */
export type FeedSearch = { page?: number };

/**
 * @desc The router's `validateSearch` for both listings, following `parseListingSearch`: keeps a
 * valid page, drops the rest, never throws, omits the default — a stale or hand-edited link
 * degrades to page 1 rather than an error.
 */
export const parseFeedSearch = (raw: Record<string, unknown>): FeedSearch => {
  const page = numberParam(raw.page);
  return page !== undefined && Number.isInteger(page) && page > 1 ? { page } : {};
};

/** @desc Pages in a feed of `total` rows, at least one (so "Page 1 of 1" over an empty feed). */
export const feedPageCount = (total: number, limit = FEED_PAGE_SIZE): number =>
  Math.max(1, Math.ceil(total / limit));

/** @desc One page of stories for `/news`: every kind but platform announcements, as on Home. */
export const useNewsPage = (page: number): LiveQuery<News.Page> =>
  liveQuery(api.news.list.useQuery({ page, limit: FEED_PAGE_SIZE, kinds: NEWS_FEED_KINDS }));

/** @desc One page of activity for `/updates`: platform events and the caller's own follows. */
export const useActivityPage = (page: number): LiveQuery<Activity.Page> =>
  liveQuery(api.activity.list.useQuery({ page, limit: FEED_PAGE_SIZE }));
